import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export const SUPPORTED_LANGUAGES = [
  'es',
  'en',
  'ca',
  'eu',
  'gl',
  'ca-valencia',
] as const;

export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_OPTIONS: ReadonlyArray<{ code: AppLanguage; label: string }> = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'ca', label: 'Català' },
  { code: 'eu', label: 'Euskera' },
  { code: 'gl', label: 'Galego' },
  { code: 'ca-valencia', label: 'Valencià' },
];

const STORAGE_KEY = 'appLang';
const DEFAULT_LANGUAGE: AppLanguage = 'es';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly languageSubject = new BehaviorSubject<AppLanguage>(DEFAULT_LANGUAGE);
  readonly languageChanges = this.languageSubject.asObservable();

  constructor(private translate: TranslateService) {}

  async init(): Promise<void> {
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    const lang = this.getStoredLanguage();
    await firstValueFrom(this.translate.use(lang));
    this.applyLanguage(lang, false);
  }

  getCurrentLanguage(): AppLanguage {
    const current = this.translate.currentLang || this.languageSubject.value;
    return this.normalizeLanguage(current);
  }

  async setLanguage(lang: AppLanguage): Promise<void> {
    if (!this.isSupportedLanguage(lang)) {
      return;
    }
    await firstValueFrom(this.translate.use(lang));
    this.applyLanguage(lang, true);
  }

  private applyLanguage(lang: AppLanguage, persist: boolean): void {
    if (persist) {
      localStorage.setItem(STORAGE_KEY, lang);
    }
    document.documentElement.lang = lang;
    this.languageSubject.next(lang);
  }

  private getStoredLanguage(): AppLanguage {
    return this.normalizeLanguage(localStorage.getItem(STORAGE_KEY));
  }

  private normalizeLanguage(lang: string | null | undefined): AppLanguage {
    return this.isSupportedLanguage(lang) ? lang : DEFAULT_LANGUAGE;
  }

  private isSupportedLanguage(lang: string | null | undefined): lang is AppLanguage {
    return !!lang && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
  }
}
